// AssignModal.jsx — new component
// Save to /home/apps/lead-manager-frontend/src/components/AssignModal.jsx

import React, { useState, useMemo } from 'react';

export default function AssignModal({ user, tree, onClose, onAssigned }) {
  const [assignmentType, setAssignmentType] = useState(
    user.introducer_id ? 'introducer' : user.broker_company_id ? 'broker' : 'broker'
  );
  const [brokerCompanyId, setBrokerCompanyId] = useState(user.broker_company_id || '');
  const [brokerLevel, setBrokerLevel] = useState(user.broker_level || 'adviser');
  const [managerId, setManagerId] = useState(user.manager_id || '');
  const [introducerId, setIntroducerId] = useState(user.introducer_id || '');
  const [introducerLevel, setIntroducerLevel] = useState(user.introducer_level || 'adviser');
  const [introducerParentId, setIntroducerParentId] = useState(user.introducer_parent_id || '');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  // Available managers for selected broker company
  const managersInCompany = useMemo(() => {
    if (!brokerCompanyId) return [];
    const co = tree.brokerCompanies.find(c => c.id === brokerCompanyId);
    if (!co) return [];
    return co.users
      .filter(u => u.broker_level === 'manager' || u.broker_level === 'owner')
      .filter(u => u.id !== user.id);
  }, [brokerCompanyId, tree, user.id]);

  // All introducers across all broker companies (deduped)
  const allIntroducers = useMemo(() => {
    const seen = new Map();
    tree.brokerCompanies.forEach(c => {
      c.introducers.forEach(i => {
        if (!seen.has(i.id)) seen.set(i.id, i);
      });
    });
    return Array.from(seen.values());
  }, [tree]);

  // Available parents within selected introducer
  const parentsInIntroducer = useMemo(() => {
    if (!introducerId) return [];
    const intro = allIntroducers.find(i => i.id === introducerId);
    if (!intro) return [];
    return intro.users
      .filter(u => u.introducer_level === 'manager' || u.introducer_level === 'company')
      .filter(u => u.id !== user.id);
  }, [introducerId, allIntroducers, user.id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    const body = { assignmentType };
    if (assignmentType === 'broker') {
      if (!brokerCompanyId) { setError('Select a broker company'); setSaving(false); return; }
      body.broker_company_id = brokerCompanyId;
      body.broker_level = brokerLevel;
      body.manager_id = managerId || null;
    } else if (assignmentType === 'introducer') {
      if (!introducerId) { setError('Select an introducer company'); setSaving(false); return; }
      body.introducer_id = introducerId;
      body.introducer_level = introducerLevel;
      body.introducer_parent_id = introducerParentId || null;
    }

    try {
      const response = await fetch(`/api/owner/users/${user.id}/assign`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });
      const data = await response.json();
      if (response.ok) {
        onAssigned();
        onClose();
      } else {
        setError(data.error || 'Failed to assign');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleUnassign = async () => {
    if (!window.confirm(`Remove ${user.name} from their current company? They'll appear as unassigned.`)) return;
    setSaving(true);
    try {
      const response = await fetch(`/api/owner/users/${user.id}/assign`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ assignmentType: 'unassign' })
      });
      if (response.ok) {
        onAssigned();
        onClose();
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to unassign');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const isCurrentlyAssigned = !!(user.broker_company_id || user.introducer_id);

  return (
    <div className="bc-modal" onClick={onClose}>
      <div className="bc-modal__panel bc-modal__panel--wide" onClick={(e) => e.stopPropagation()}>
        <h3>{isCurrentlyAssigned ? 'Move' : 'Assign'} — {user.name}</h3>
        <p className="bc-modal__sub">{user.email}</p>

        {error && <div className="bc-alert bc-alert--error">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="bc-assign-tabs">
            <button
              type="button"
              className={`bc-assign-tab ${assignmentType === 'broker' ? 'is-active' : ''}`}
              onClick={() => setAssignmentType('broker')}
            >Broker company</button>
            <button
              type="button"
              className={`bc-assign-tab ${assignmentType === 'introducer' ? 'is-active' : ''}`}
              onClick={() => setAssignmentType('introducer')}
            >Introducer</button>
          </div>

          {assignmentType === 'broker' && (
            <>
              <label>Broker company
                <select className="bc-select" value={brokerCompanyId} onChange={(e) => { setBrokerCompanyId(e.target.value); setManagerId(''); }} required>
                  <option value="">— Select —</option>
                  {tree.brokerCompanies.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </label>
              <label>Level
                <select className="bc-select" value={brokerLevel} onChange={(e) => setBrokerLevel(e.target.value)}>
                  <option value="owner">Owner</option>
                  <option value="manager">Manager</option>
                  <option value="adviser">Adviser</option>
                </select>
              </label>
              {brokerLevel === 'adviser' && managersInCompany.length > 0 && (
                <label>Reports to (optional)
                  <select className="bc-select" value={managerId} onChange={(e) => setManagerId(e.target.value)}>
                    <option value="">— No manager —</option>
                    {managersInCompany.map(m => (
                      <option key={m.id} value={m.id}>{m.name} ({m.broker_level})</option>
                    ))}
                  </select>
                </label>
              )}
            </>
          )}

          {assignmentType === 'introducer' && (
            <>
              <label>Introducer company
                <select className="bc-select" value={introducerId} onChange={(e) => { setIntroducerId(e.target.value); setIntroducerParentId(''); }} required>
                  <option value="">— Select —</option>
                  {allIntroducers.map(i => (
                    <option key={i.id} value={i.id}>{i.name}</option>
                  ))}
                </select>
              </label>
              <label>Level
                <select className="bc-select" value={introducerLevel} onChange={(e) => setIntroducerLevel(e.target.value)}>
                  <option value="company">Company</option>
                  <option value="manager">Manager</option>
                  <option value="solo">Solo</option>
                  <option value="adviser">Adviser</option>
                </select>
              </label>
              {(introducerLevel === 'adviser' || introducerLevel === 'manager') && parentsInIntroducer.length > 0 && (
                <label>Reports to (optional)
                  <select className="bc-select" value={introducerParentId} onChange={(e) => setIntroducerParentId(e.target.value)}>
                    <option value="">— No parent —</option>
                    {parentsInIntroducer.map(p => (
                      <option key={p.id} value={p.id}>{p.name} ({p.introducer_level})</option>
                    ))}
                  </select>
                </label>
              )}
            </>
          )}

          <div className="bc-modal__actions">
            {isCurrentlyAssigned && (
              <button type="button" className="bc-btn bc-btn--danger-ghost" onClick={handleUnassign} disabled={saving}>
                Unassign
              </button>
            )}
            <div style={{ flex: 1 }} />
            <button type="button" className="bc-btn" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="submit" className="bc-btn bc-btn--primary" disabled={saving}>
              {saving ? 'Saving…' : (isCurrentlyAssigned ? 'Move user' : 'Assign')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
